import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UI, btnSx } from "../components/ActionDrawer";
import DashTable, { PageHead, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const rupee = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const PAGE_SIZE = 15;

const EMI_STATUS_LABEL = {
  documents_pending: "EMI: sent to Massfintech",
  under_review: "EMI: under review",
  approved: "EMI approved",
  rejected: "EMI rejected",
};

/* Orders that reached the purchase-agreement-eligible stage (advance paid,
   or EMI approved). Sending the agreement is the real gate — it moves the
   order to Ready to Ship — while "Mark signed" just records the
   countersigned copy once it's back, without blocking anything downstream. */
const PurchaseAgreements = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [signingId, setSigningId] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  const fetchRows = async (targetPage = page) => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchAgreementActionItems(token, {
        page: targetPage,
        limit: PAGE_SIZE,
      });
      setRows(res.data || []);
      setPagination(res.pagination || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching purchase agreements");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleMarkSigned = async (orderId) => {
    setSigningId(orderId);
    try {
      await apiService.setAgreementSigned(orderId, true, token);
      toast.success("Purchase agreement marked as signed");
      fetchRows();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to mark agreement signed",
      );
    } finally {
      setSigningId(null);
    }
  };

  const handleSend = async (orderId) => {
    setSendingId(orderId);
    try {
      await apiService.sendPurchaseAgreement(orderId, token);
      toast.success("Purchase agreement marked as sent — order can move to Ready to Ship");
      fetchRows();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to mark agreement sent",
      );
    } finally {
      setSendingId(null);
    }
  };

  const kpis = pagination ? [{ label: "Needs action", value: pagination.total, alarm: pagination.total > 0 }] : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Purchase Agreements"
        lede="Orders that have reached the purchase-agreement stage — advance paid, or EMI approved. Sending the agreement unlocks Ready to Ship and the rest of the pipeline; signing is recorded separately once the countersigned copy is back."
        kpis={kpis}
      />

      <DashTable
        columns={[
          { label: "Date" },
          { label: "Clinic" },
          { label: "Doctor" },
          { label: "Lead owner" },
          { label: "Payment" },
          { label: "Agreement" },
          { label: "Order total", num: true },
          { label: "Action" },
        ]}
        rows={rows.map((row) => ({
          id: row.orderId,
          cells: [
            shortDate(row.createdAt),
            row.clinicName || "—",
            row.doctorName || "—",
            row.leadOwner || "—",
            <Tag tone={row.paymentType === "emi" ? "bl" : "g"}>
              {row.paymentType === "emi"
                ? EMI_STATUS_LABEL[row.emiStatus] || "EMI"
                : "Advance paid"}
            </Tag>,
            <Tag tone={row.sent ? "bl" : "s"}>{row.sent ? "Sent" : "Not sent"}</Tag>,
            rupee(row.quoteTotal),
            row.sent ? (
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkSigned(row.orderId);
                }}
                disabled={signingId === row.orderId}
                sx={btnSx("ghost")}
              >
                {signingId === row.orderId ? "Marking…" : "Mark signed"}
              </Box>
            ) : (
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSend(row.orderId);
                }}
                disabled={sendingId === row.orderId}
                sx={btnSx("primary", sendingId === row.orderId)}
              >
                {sendingId === row.orderId ? "Sending…" : "Send"}
              </Box>
            ),
          ],
        }))}
        onRowClick={(id) => navigate(`/dashboard/orders/${id}`)}
        emptyText="No orders waiting on the agreement."
        errorText={loadFailed ? "Could not load purchase agreements." : null}
        onRetry={fetchRows}
        foot={`${rows.length} order${rows.length === 1 ? "" : "s"} waiting on the agreement`}
      />

      {pagination && pagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={pagination.page <= 1}
                sx={btnSx("ghost", pagination.page <= 1)}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
                sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
              >
                Next
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default PurchaseAgreements;
